import { Trans } from '@lingui/macro'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Slider,
  Typography
} from '@mui/material'
import CustomDatePickerWithFormik from 'app/views/common-components/date-pickers/CustomDatePickerWithFormik'
import { DialogTitleWithIconClose } from 'app/views/common-components/DialogTitleWithIconClose'
import { FormTopLabel } from 'app/views/common-components/forms/FormTopLabel'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import { INVALID_DATE_ERROR, requiredTrans } from 'app/views/forms/formTranslations'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'

export interface AddOrEditActionDialogProps {
  open: boolean
  create?: boolean
  card?: {
    title?: string
    description?: string
    category?: string
    expectedOutcome?: string
    dueDate?: string
    success?: number
    cardIndex?: number
    columnIndex?: number
    failReason?: string
  }
  handleClose: () => void
  handleSave: (
    values: { title: string, description: string, [key: string]: any },
    columnIndex: number,
    cardIndex: number
  ) => void
  handleAddNewCard?: (values: { title: string, description: string }) => void
  isOnHold?: boolean
}

export default function AddOrEditActionDialog({
  open,
  create = false,
  card = {},
  handleClose,
  handleSave,
  handleAddNewCard,
  isOnHold = false
}: AddOrEditActionDialogProps): JSX.Element {
  const dialogTitle = create
    ? (
      <Trans>ACTION_PLAN_CREATE_ACTION_DIALOG_TITLE</Trans>
    )
    : (
      <Trans>ACTION_PLAN_EDIT_ACTION_DIALOG_TITLE</Trans>
    )
  const categoryList = [
    {
      label: <Trans>Property Management</Trans>,
      value: 'Property Management'
    },
    {
      label: <Trans>Financial Managment</Trans>,
      value: 'Financial Managment'
    },
    {
      label: <Trans>Governance</Trans>,
      value: 'Governance'
    },
    {
      label: <Trans>Social and community relations</Trans>,
      value: 'Social and community relations'
    }
  ]

  return (
    <Dialog open={open} fullWidth maxWidth='xs' onClose={handleClose}>
      <DialogTitleWithIconClose
        label={dialogTitle}
        handleClose={handleClose}
        sx={{ paddingBottom: '18px' }}
      />
      <Formik
        key={
          create
            ? 'create'
            : `${card?.columnIndex ?? 0}-${card?.cardIndex ?? 0}`
        }
        initialValues={{
          title: card?.title ?? '',
          description: card?.description ?? '',
          expectedOutcome: card?.expectedOutcome ?? '',
          category: card?.category ?? '',
          dueDate: card?.dueDate ?? null,
          success: card?.success ?? 1,
          failReason: card?.failReason ?? ''
        }}
        validationSchema={Yup.object({
          title: Yup.string().required(requiredTrans),
          description: Yup.string().required(requiredTrans).max(1000),
          expectedOutcome: Yup.string().required(requiredTrans).max(1000),
          category: Yup.string().nullable().required(requiredTrans),
          dueDate: Yup.date().typeError(INVALID_DATE_ERROR).required(requiredTrans).nullable(),
          success: Yup.number().required(requiredTrans)
        })}
        onSubmit={(values) => {
          if (create && handleAddNewCard !== undefined) {
            handleAddNewCard(values)
          } else {
            handleSave(
              { ...card, ...values },
              card?.columnIndex ?? 0,
              card?.cardIndex ?? 0
            )
          }
          handleClose()
        }}
        validateOnMount
      >
        {({
          values,
          setFieldValue,
          setFieldTouched,
          errors,
          touched,
          isValid
        }) => (
          <Form>
            <DialogContent sx={{ padding: '0 48px 32px 48px' }}>
              <Typography
                sx={{
                  paddingTop: '14px',
                  paddingBottom: '4px',
                  fontSize: '12px',
                  fontWeight: 600,
                  lineHeight: '16px'
                }}
              >
                <Trans>ACTION_PLAN_REQUIRED_PROMPT</Trans>
              </Typography>

              <Box sx={{ marginTop: '8px' }}>
                <FormTopLabel required>
                  <Trans>ACTION_PLAN_ACTION_NAME_LABEL</Trans>{' '}
                </FormTopLabel>
                <TextFieldWithFormik
                  required
                  name='title'
                  fullWidth
                  variant='outlined'
                  sx={{
                    '& .MuiOutlinedInput-root fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.40)'
                    }
                  }}
                />
              </Box>

              <Box sx={{ marginTop: '28px' }}>
                <FormTopLabel required>
                  <Trans>ACTION_PLAN_ACTION_DESCRIPTION_LABEL</Trans>{' '}
                </FormTopLabel>
                <TextFieldWithFormik
                  required
                  name='description'
                  fullWidth
                  variant='outlined'
                  multiline
                  minRows={2}
                  sx={{
                    '& .MuiOutlinedInput-root fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.40)'
                    }
                  }}
                />
              </Box>

              <Box sx={{ marginTop: '28px' }}>
                <FormTopLabel required>
                  <Trans>ACTION_PLAN_EXPECTED_OUTCOME_LABEL</Trans>{' '}
                </FormTopLabel>
                <TextFieldWithFormik
                  required
                  name='expectedOutcome'
                  fullWidth
                  variant='outlined'
                  multiline
                  minRows={2}
                  sx={{
                    '& .MuiOutlinedInput-root fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.40)'
                    }
                  }}
                />
              </Box>

              {isOnHold && (
                <Box sx={{ marginTop: '28px' }}>
                  <FormTopLabel required>
                    <Trans>ACTION_PLAN_REASON_FOR_HOLD_TITLE</Trans>{' '}
                  </FormTopLabel>
                  <TextFieldWithFormik
                    required
                    name='failReason'
                    fullWidth
                    variant='outlined'
                    multiline
                    minRows={1}
                    sx={{
                      '& .MuiOutlinedInput-root fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.40)'
                      }
                    }}
                  />
                </Box>)}

              <Box sx={{ marginTop: '28px' }}>
                <FormTopLabel required>
                  <Trans>ACTION_PLAN_CATEGORY_LABEL</Trans>{' '}
                </FormTopLabel>
                <TextFieldWithFormik
                  required
                  select
                  name='category'
                  fullWidth
                  variant='outlined'
                  sx={{
                    '& .MuiOutlinedInput-root fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.40)'
                    }
                  }}
                >
                  {categoryList.map((obj) => {
                    return (
                      <MenuItem value={obj.value} key={obj.value}>
                        <Trans>{obj.label}</Trans>
                      </MenuItem>
                    )
                  })}
                </TextFieldWithFormik>
              </Box>

              <Box sx={{ marginTop: '28px' }}>
                <FormTopLabel required>
                  <Trans>ACTION_PLAN_DUE_DATE_LABEL</Trans>{' '}
                </FormTopLabel>
                <CustomDatePickerWithFormik name='dueDate' minDate={new Date()} />
              </Box>

              {card?.columnIndex === 2 && (
                <div style={{ marginTop: 16 }}>
                  <FormTopLabel required>
                    <Trans>Success Measure</Trans>{' '}
                  </FormTopLabel>
                  <Field name='success'>
                    {({ field, form }) => (
                      <Slider
                        {...field}
                        value={field.value ?? 1}
                        onChange={(e, newValue) =>
                          form.setFieldValue(field.name, newValue)}
                        step={1}
                        min={1}
                        max={5}
                        marks={[
                          { value: 1, label: '1' },
                          { value: 2, label: '2' },
                          { value: 3, label: '3' },
                          { value: 4, label: '4' },
                          { value: 5, label: '5' }
                        ]}
                        valueLabelDisplay='auto'
                      />
                    )}
                  </Field>
                </div>
              )}
            </DialogContent>

            <DialogActions
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: '0 48px 32px 48px',
                gap: 2,
                '> :not(style) ~ :not(style)': { marginLeft: 0 }
              }}
            >
              <Button
                fullWidth
                type='submit'
                variant='contained'
                color='primary'
                disabled={!isValid}
              >
                {create
                  ? (
                    <Trans>ACTION_PLAN_CREATE_LABEL</Trans>
                  )
                  : (
                    <Trans>ACTION_PLAN_SAVE_CHANGES_LABEL</Trans>
                  )}
              </Button>
              <Button
                onClick={handleClose}
                fullWidth
                variant='outlined'
                sx={{ marginLeft: 0 }}
              >
                <Trans>ACTION_PLAN_CANCEL_LABEL</Trans>
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}
